import axios from 'axios';
import siteSetting from '../config/env';

/**
 * Multi-location: a device's location is per sign-in (per access token).
 *
 * The API answers "This device has switched location. Refresh and try again."
 * when a request names a location this device is no longer on: it was moved
 * off a site the person left or lost access to, or the page still holds the
 * previous site's id. Before this, every request on that screen failed with
 * that message and nothing recovered it.
 *
 * Installed once from src/index.js, before any service module is loaded, so
 * it sees every call: it adds a response interceptor to the default axios
 * instance, to every instance made with axios.create (the _services modules
 * make their own), and wraps window.fetch (the fetch-based services). It
 * only reads responses; nothing about any request changes.
 *
 * On that message: ask the API which site this device is on now, drop the
 * per-site caches (same list as switchToSite), store the site and reload.
 * At most once every 30 seconds, so a response that keeps saying it can
 * never loop the page.
 */
const SWITCHED = 'switched location';
const LAST_KEY = 'ml:siteRefreshAt';
const SITE_CACHES = ['receptionModeSettings', 'cardBookingDate', 'diaryStaffOrder', 'salonTimezone', 'isShow', 'subscriptionGate', 'ml:sites', 'beu-access'];
let refreshing = false;

function messageOf(body) {
    if (!body || typeof body !== 'object') return '';
    const e = body.error;
    return String((e && (e.responseMessage || e.message)) || body.message || '');
}

function isSwitched(body) {
    return messageOf(body).toLowerCase().indexOf(SWITCHED) !== -1;
}

async function refreshSiteContext() {
    if (refreshing) return;
    try {
        const last = Number(sessionStorage.getItem(LAST_KEY) || 0);
        if (Date.now() - last < 30 * 1000) return;
        sessionStorage.setItem(LAST_KEY, String(Date.now()));
    } catch (e) { /* storage blocked: still refresh once for this page */ }
    refreshing = true;
    const previous = localStorage.getItem('salonId');
    let active = null;
    try {
        const r = await axios.get(siteSetting.api.v2 + 'sites/mine', {
            headers: { 'Content-Type': 'application/json', accessToken: localStorage.getItem('accessToken') || '' }
        });
        const d = r && r.data;
        const data = d && (d.responseData !== undefined ? d.responseData : d.data !== undefined ? d.data : d);
        active = data && data.activeSalonId ? String(data.activeSalonId) : null;
    } catch (e) { /* reload anyway: the page re-reads its site */ }
    SITE_CACHES.forEach((k) => { try { localStorage.removeItem(k); } catch (e) { /* ignore */ } });
    try { sessionStorage.removeItem('ml:sites'); } catch (e) { /* ignore */ }
    // The header Location picker's selection belongs to the old site.
    ['loc:viewing', 'fin:salonIds', 'mkt:salonIds'].forEach((k) => { try { sessionStorage.removeItem(k); } catch (e) { /* ignore */ } });
    if (active) localStorage.setItem('salonId', active);
    const path = window.location.pathname || '/';
    // A page addressed by the old site (e.g. /diary/<oldSiteId>) moves with it.
    if (active && previous && previous !== active && path.indexOf(previous) !== -1) {
        window.location.href = path.split(previous).join(active) + (window.location.search || '');
    } else {
        window.location.reload();
    }
}

function onAxiosResponse(response) {
    if (response && isSwitched(response.data)) refreshSiteContext();
    return response;
}
function onAxiosError(error) {
    if (error && error.response && isSwitched(error.response.data)) refreshSiteContext();
    return Promise.reject(error);
}

function attach(instance) {
    try { instance.interceptors.response.use(onAxiosResponse, onAxiosError); } catch (e) { /* ignore */ }
    return instance;
}

let installed = false;
export function installSiteSwitchGuard() {
    if (installed || typeof window === 'undefined') return;
    installed = true;
    attach(axios);
    const create = axios.create.bind(axios);
    axios.create = function patchedCreate(config) { return attach(create(config)); };

    const v2 = String(siteSetting.api.v2 || '');
    if (typeof window.fetch === 'function' && v2) {
        const nativeFetch = window.fetch.bind(window);
        window.fetch = function guardedFetch(input, init) {
            return nativeFetch(input, init).then((res) => {
                try {
                    const url = typeof input === 'string' ? input : (input && input.url) || '';
                    const type = (res && res.headers && res.headers.get('content-type')) || '';
                    if (url.indexOf(v2) === 0 && type.indexOf('json') !== -1) {
                        // Read a copy, off the caller's path: the caller's body is untouched.
                        res.clone().text().then((text) => {
                            if (text && text.indexOf(SWITCHED) !== -1) {
                                try { if (isSwitched(JSON.parse(text))) refreshSiteContext(); } catch (e) { /* not JSON */ }
                            }
                        }).catch(() => {});
                    }
                } catch (e) { /* never break a request */ }
                return res;
            });
        };
    }
}

installSiteSwitchGuard();

export default installSiteSwitchGuard;
